import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  defaultAnimateLayoutChanges,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Divider, Tooltip } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { CrossSvg, PlusSvg } from '../../../../assets/icons';
import theme from '../../../../assets/theme';
import {
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../../hooks/useDesignerLogic';
import { useDesignerUIContext } from '../../../../hooks/useDesignerUIState';
import {
  ById,
  Prompt,
  PromptItemProperty,
  PromptOutputProperty,
  PromptOutputPropertyItemType,
  PromptSingleTestCaseResult,
} from '../../../../services/backendService/types';
import { generateUniqueId } from '../../../../utils/general';
import Select from '../../../_atoms/_Select/Select';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import SearchInput from '../../../_molecules/SearchInput';
import { SlideElement } from '../../../SlideComponents/slide.types';
import { PromptAIConnector } from './PromptAIConnector';

const PromptOutputPropertyItemBuilder = ({
  property,
  prompt,
  onOutputChanged,
  promptDependentInputs,
  onAddAiText,
  selectedElementId,
}: {
  property: PromptOutputProperty;
  prompt: Prompt;
  onOutputChanged: () => void;
  promptDependentInputs: PromptSingleTestCaseResult | undefined;
  onAddAiText: (promptSourceId: string, textValue?: string | undefined) => void;
  selectedElementId: string | undefined;
}) => {
  const localTemplateDispatch = useDesignerDataContext(c => c.dispatch);
  const itemsType = property.items?.type;

  const handleEditPropertyItemType = (itemType: PromptOutputPropertyItemType) => {
    if (!property.items) return;

    const properties = itemType === 'object' ? {} : undefined;
    localTemplateDispatch({
      type: TemplateStateActions.editPromptOutputProperty,
      promptId: prompt.id,
      promptOutputProperty: {
        ...property,
        items: {
          ...property.items,
          type: itemType,
          properties,
        },
      },
    });
    onOutputChanged();
  };

  const handleEditPromptItemProperty = (itemProperty: PromptItemProperty) => {
    if (!property.items) return;
    localTemplateDispatch({
      type: TemplateStateActions.editPromptOutputProperty,
      promptId: prompt.id,
      promptOutputProperty: {
        ...property,
        items: {
          ...property.items,
          properties: {
            ...(property.items.properties || {}),
            [itemProperty.id]: itemProperty,
          },
        },
      },
    });
  };

  const handleAddPromptItemProperty = () => {
    if (!property.items) return;

    const itemProperty: PromptItemProperty = {
      id: `item-${generateUniqueId()}`,
      description: '',
      type: 'string',
    };
    localTemplateDispatch({
      type: TemplateStateActions.editPromptOutputProperty,
      promptId: prompt.id,
      promptOutputProperty: {
        ...property,
        items: {
          ...property.items,
          properties: {
            ...(property.items.properties || {}),
            [itemProperty.id]: itemProperty,
          },
        },
      },
    });
  };
  const handleDeletePromptItemProperty = (itemPropertyId: string) => {
    if (!property.items) return;

    const properties = { ...property.items.properties };
    delete properties[itemPropertyId];

    localTemplateDispatch({
      type: TemplateStateActions.editPromptOutputProperty,
      promptId: prompt.id,
      promptOutputProperty: {
        ...property,
        items: {
          ...property.items,
          properties,
        },
      },
    });
    onOutputChanged();
  };

  if (!itemsType) {
    return <div>has no items</div>;
  }

  if (itemsType === 'string' || itemsType === 'number') {
    return (
      <>
        <div style={{ display: 'flex', marginTop: 5 }}>
          <div style={{ flex: 1, paddingRight: 25 }}>
            <Select
              onSelect={val => {
                handleEditPropertyItemType(val as PromptOutputPropertyItemType);
              }}
              label={'Type'}
              value={itemsType}
              style={{ width: '100%' }}
              options={[
                {
                  displayName: 'Text',
                  value: 'string',
                },
                {
                  displayName: 'Object',
                  value: 'object',
                },
              ]}
            ></Select>
          </div>
        </div>
        {promptDependentInputs && (
          <div>
            <PromptOutputArrayItems
              promptDependentInputs={promptDependentInputs}
              property={property}
              promptId={prompt.id}
              onAddAiText={onAddAiText}
              selectedElementId={selectedElementId}
            />
          </div>
        )}
      </>
    );
  }

  if (itemsType === 'object') {
    if (!property.items) return null;
    return (
      <>
        <div style={{ display: 'flex', marginTop: 5 }}>
          <div style={{ flex: 1, paddingRight: 5 }}>
            <Select
              onSelect={val => {
                handleEditPropertyItemType(val as PromptOutputPropertyItemType);
              }}
              style={{ width: '100%' }}
              value={itemsType}
              options={[
                {
                  displayName: 'Text',
                  value: 'string',
                },
                {
                  displayName: 'Object',
                  value: 'object',
                },
              ]}
            ></Select>
          </div>
          <div style={{ alignItems: 'center', display: 'flex' }}>
            <StyledButton
              $color={theme.colors.green}
              onClick={() => handleAddPromptItemProperty()}
            >
              +
            </StyledButton>
          </div>
        </div>
        {Object.values(property.items.properties || {}).map(
          (itemProperty: PromptItemProperty) => (
            <>
              <div
                key={itemProperty.id}
                style={{ display: 'flex', paddingLeft: 0, marginTop: 5 }}
              >
                <div style={{ paddingRight: 5 }}>
                  <Select
                    onSelect={val => {
                      handleEditPromptItemProperty({
                        ...itemProperty,
                        type: val as PromptItemProperty['type'],
                      });
                    }}
                    disabled={true}
                    label={'Type'}
                    value={itemProperty.type}
                    style={{ width: '75px' }}
                    options={[
                      {
                        displayName: 'Text',
                        value: 'string',
                      },
                    ]}
                  ></Select>
                </div>
                <div style={{ paddingRight: 5, width: '100%' }}>
                  <SearchInput
                    icon={null}
                    label={'Description'}
                    style={{ width: '100%' }}
                    placeholder={'Describe this ouput field'}
                    value={itemProperty.description}
                    onChange={val => {
                      const replacedValues = val.replace(/[^0-9a-zA-Z\s]+/g, '');

                      handleEditPromptItemProperty({
                        ...itemProperty,
                        description: replacedValues,
                      });
                    }}
                  />
                </div>
                <div
                  style={{ alignItems: 'flex-end', display: 'flex', paddingBottom: 10 }}
                >
                  <StyledButton
                    $color={theme.colors.red}
                    onClick={() => handleDeletePromptItemProperty(itemProperty.id)}
                  >
                    <CrossSvg />
                  </StyledButton>
                </div>
              </div>
              {promptDependentInputs && (
                <div>
                  <PromptOutputArrayItems
                    promptDependentInputs={promptDependentInputs}
                    property={property}
                    promptId={prompt.id}
                    onAddAiText={onAddAiText}
                    itemId={itemProperty.id}
                    selectedElementId={selectedElementId}
                  />
                </div>
              )}
            </>
          ),
        )}
      </>
    );
  }

  return null;
};

const PromptOutputProperty = ({
  property,
  prompt,
  disableArray,
  onOutputChanged,
  onAddAiText,
  promptDependentInputs,
  selectedElementId,
}: {
  property: PromptOutputProperty;
  prompt: Prompt;
  disableArray?: boolean;
  onOutputChanged: () => void;
  onAddAiText: (promptSourceId: string, textValue?: string | undefined) => void;
  promptDependentInputs: PromptSingleTestCaseResult | undefined;
  selectedElementId: string | undefined;
}) => {
  const localTemplateDispatch = useDesignerDataContext(c => c.dispatch);

  const validOptions = useMemo(
    () =>
      [
        {
          displayName: 'Text',
          value: 'string',
        },
        {
          displayName: 'List',
          value: 'array',
        },
      ].filter(option => (disableArray ? option.value !== 'array' : true)),
    [disableArray],
  );

  if (property.type === 'string' || property.type === 'number') {
    return (
      <div style={{ paddingLeft: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ paddingRight: 5 }}>
            <Select
              label={'Type'}
              style={{ width: '80px' }}
              onSelect={val => {
                localTemplateDispatch({
                  type: TemplateStateActions.editPromptOutputProperty,
                  promptId: prompt.id,
                  promptOutputProperty: {
                    ...property,
                    type: val as PromptOutputProperty['type'],
                    items: val === 'array' ? { type: 'string', maxLength: 3 } : undefined,
                  },
                });
                onOutputChanged();
              }}
              value={property.type}
              options={validOptions}
            ></Select>
          </div>
          <div style={{ flex: 1, paddingRight: 5, width: '100%' }}>
            <SearchInput
              icon={null}
              style={{ width: '100%' }}
              label={'Description'}
              placeholder={'Describe this ouput field'}
              value={property.description}
              onChange={val => {
                const replacedValues = val.replace(/[^0-9a-zA-Z\s]+/g, '');
                localTemplateDispatch({
                  type: TemplateStateActions.editPromptOutputProperty,
                  promptId: prompt.id,
                  promptOutputProperty: { ...property, description: replacedValues },
                });
                onOutputChanged();
              }}
            />
          </div>
          <div style={{ alignItems: 'flex-end', display: 'flex', paddingBottom: 10 }}>
            <StyledButton
              $color={theme.colors.red}
              onClick={() => {
                localTemplateDispatch({
                  type: TemplateStateActions.deletePromptOutputProperty,
                  promptId: prompt.id,
                  promptOutputPropertyId: property.id,
                });
                onOutputChanged();
              }}
            >
              <CrossSvg />
            </StyledButton>
          </div>
        </div>
      </div>
    );
  }

  if (property.type === 'array' && !disableArray) {
    return (
      <div style={{ paddingLeft: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ paddingRight: 5 }}>
            <Select
              style={{ minWidth: '60px' }}
              label={'Type'}
              onSelect={val => {
                localTemplateDispatch({
                  type: TemplateStateActions.editPromptOutputProperty,
                  promptId: prompt.id,
                  promptOutputProperty: {
                    ...property,
                    type: val as PromptOutputProperty['type'],
                    items: undefined,
                  },
                });
                onOutputChanged();
              }}
              value={property.type}
              options={[
                {
                  displayName: 'Text',
                  value: 'string',
                },

                {
                  displayName: 'List',
                  value: 'array',
                },
              ]}
            ></Select>
          </div>
          <div style={{ flex: 0, paddingRight: 5 }}>
            <div>
              <Select
                onSelect={val => {
                  if (!property.items) return;
                  localTemplateDispatch({
                    type: TemplateStateActions.editPromptOutputProperty,
                    promptId: prompt.id,
                    promptOutputProperty: {
                      ...property,
                      items: {
                        ...property.items,
                        maxLength: val as number,
                      },
                    },
                  });
                  onOutputChanged();
                }}
                style={{ width: '65px' }}
                label={'Length'}
                value={property.items?.maxLength}
                options={[
                  {
                    displayName: '2',
                    value: 2,
                  },
                  {
                    displayName: '3',
                    value: 3,
                  },
                  {
                    displayName: '4',
                    value: 4,
                  },
                  {
                    displayName: '5',
                    value: 5,
                  },
                  {
                    displayName: '6',
                    value: 6,
                  },
                  {
                    displayName: '7',
                    value: 7,
                  },
                  {
                    displayName: '8',
                    value: 8,
                  },
                  {
                    displayName: '9',
                    value: 9,
                  },
                  {
                    displayName: '10',
                    value: 10,
                  },
                ]}
              ></Select>
            </div>
          </div>
          <div style={{ flex: 1, paddingRight: 5, width: '100%' }}>
            <SearchInput
              icon={null}
              value={property?.description}
              label={'Description'}
              style={{ width: '100%' }}
              placeholder={'Description'}
              onChange={val => {
                const replacedValues = val.replace(/[^0-9a-zA-Z\s]+/g, '');

                localTemplateDispatch({
                  type: TemplateStateActions.editPromptOutputProperty,
                  promptId: prompt.id,
                  promptOutputProperty: { ...property, description: replacedValues },
                });
              }}
            />
          </div>
          <div
            style={{
              alignItems: 'flex-end',
              display: 'flex',
              paddingBottom: 10,
            }}
          >
            <StyledButton
              $color={theme.colors.red}
              onClick={() => {
                localTemplateDispatch({
                  type: TemplateStateActions.deletePromptOutputProperty,
                  promptId: prompt.id,
                  promptOutputPropertyId: property.id,
                });
                onOutputChanged();
              }}
            >
              <CrossSvg />
            </StyledButton>
          </div>
        </div>
        {/** Array item properties below */}
        <div style={{ paddingLeft: 20 }}>
          <PromptOutputPropertyItemBuilder
            promptDependentInputs={promptDependentInputs}
            onAddAiText={onAddAiText}
            onOutputChanged={onOutputChanged}
            prompt={prompt}
            property={property}
            selectedElementId={selectedElementId}
          />
        </div>
      </div>
    );
  }

  // Should never get here
  return null;
};

const PromptOutputBuilder = ({
  prompt,
  onOutputChanged,
  promptDependentInputs,
  onAddAiText,
  selectedElement,
}: {
  prompt: Prompt;
  onOutputChanged: () => void;
  promptDependentInputs: PromptSingleTestCaseResult | undefined;
  onAddAiText: (promptSourceId: string, textValue?: string | undefined) => void;
  selectedElement: SlideElement | null;
}) => {
  const localTemplateDispatch = useDesignerDataContext(c => c.dispatch);
  const showTabPaneFullWidth = useDesignerUIContext(c => c.state.showTabPaneFullWidth);
  const [currentlyDragging, setCurrentlyDragging] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Require the mouse to move by 10 pixels before activating
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      // Press delay of 250ms, with tolerance of 5px of movement
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  const orderedPromptOutputProperties = useMemo(() => {
    //Check if prompt output has propertiesOrder
    if (prompt.promptOutput.propertiesOrder) {
      return prompt.promptOutput.propertiesOrder.reduce<ById<PromptOutputProperty>>(
        (acc, cur) => {
          return {
            ...acc,
            [cur]: prompt.promptOutput.properties[cur],
          };
        },
        {},
      );
    }
    return prompt.promptOutput.properties;
  }, [prompt.promptOutput.properties, prompt.promptOutput.propertiesOrder]);

  const handleDragStart = (event: DragEndEvent) => {
    const { active } = event;
    if (!active.id) return;
    setCurrentlyDragging(active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active && active.id !== over.id) {
      const promptOrder = prompt.promptOutput.propertiesOrder
        ? prompt.promptOutput.propertiesOrder
        : Object.keys(orderedPromptOutputProperties);

      const oldIndex = promptOrder.indexOf(active.id as string);
      const newIndex = promptOrder.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) {
        // Don't move anything
        return;
      }
      //place the dragged item in the new index
      const newArray = [...promptOrder];
      newArray.splice(oldIndex, 1);
      newArray.splice(newIndex, 0, active.id as string);

      localTemplateDispatch({
        type: TemplateStateActions.editPromptOutputProperty,
        promptId: prompt.id,
        promptOutputProperty: prompt.promptOutput.properties[active.id as string],
        promptOutputPropertyOrder: newArray,
      });

      setCurrentlyDragging(null);
    }
  };

  return (
    <div
      style={{
        background: theme.colors.creatorBackgroundGrey,
        gap: '10px',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '10px',
        padding: 5,
      }}
    >
      <div
        style={{
          display: 'flex',
          position: 'sticky',
          zIndex: 1,
          padding: 5,
          top: 0,
          background: theme.colors.creatorBackgroundGrey,
        }}
      >
        <p style={{ marginBottom: 5, flex: 1, fontSize: 16 }}>Expected output</p>
      </div>
      <PromptOutputContainer>
        {/* The top level selector string, number, object or array */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'sticky',
            zIndex: 1,
            top: 25,
            background: theme.colors.creatorBackgroundGrey,
            paddingBottom: 5,
          }}
        >
          <div style={{ flex: 1, paddingRight: 5 }}>
            <Select
              onSelect={val => {
                localTemplateDispatch({
                  type: TemplateStateActions.changePromptOutputType,
                  outputType: val as 'string' | 'object',
                  promptId: prompt.id,
                });
                onOutputChanged();
              }}
              value={prompt.promptOutput.type}
              options={[
                {
                  displayName: 'Text',
                  value: 'string',
                },
                {
                  displayName: 'Object',
                  value: 'object',
                },
              ]}
            ></Select>
          </div>
          {prompt.promptOutput.type === 'object' && (
            <div style={{ alignItems: 'center', display: 'flex' }}>
              <StyledButton
                $color={theme.colors.green}
                onClick={() => {
                  if (prompt.promptOutput.type === 'object') {
                    // add new promptOutput property
                    localTemplateDispatch({
                      type: TemplateStateActions.addPromptOutputProperty,
                      promptId: prompt.id,
                    });
                  }
                }}
              >
                +
              </StyledButton>
            </div>
          )}
        </div>
        {prompt.promptOutput.type === 'object' && (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              autoScroll={true}
            >
              <SortableContext
                items={
                  prompt.promptOutput?.propertiesOrder
                    ? prompt.promptOutput.propertiesOrder
                    : Object.keys(orderedPromptOutputProperties)
                }
                strategy={verticalListSortingStrategy}
              >
                {Object.values(orderedPromptOutputProperties).map((property, i) => {
                  if (!property) return null;
                  if (property.type === 'number' || property.type === 'string') {
                    return (
                      <DraggableOutput id={property.id}>
                        <OutputContainer
                          key={i}
                          style={{
                            background:
                              `${prompt.id}.${property.id}` ===
                              selectedElement?.promptSourceId
                                ? `${theme.colors.purple}25`
                                : theme.colors.white,
                          }}
                        >
                          <PromptOutputProperty
                            property={property}
                            key={property.id}
                            prompt={prompt}
                            onOutputChanged={onOutputChanged}
                            promptDependentInputs={promptDependentInputs}
                            onAddAiText={onAddAiText}
                            selectedElementId={selectedElement?.promptSourceId}
                          />
                          {promptDependentInputs && (
                            <div
                              style={{
                                display: 'grid',
                                width: '100%',
                                gridTemplateColumns: '3fr 1fr',
                                alignItems: 'center',
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <p
                                  style={{
                                    fontSize: 14,
                                    marginTop: 5,
                                    fontWeight: 'normal',
                                  }}
                                >
                                  {promptDependentInputs[`${prompt.id}.${property.id}`] &&
                                  promptDependentInputs[`${prompt.id}.${property.id}`]
                                    .value ? (
                                    promptDependentInputs[`${prompt.id}.${property.id}`]
                                      .value
                                  ) : (
                                    <span style={{ color: theme.colors.red }}>
                                      No text generated
                                    </span>
                                  )}
                                </p>
                              </div>

                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  height: '100%',
                                }}
                              >
                                {!showTabPaneFullWidth &&
                                  promptDependentInputs?.[`${prompt.id}.${property.id}`]
                                    ?.value && (
                                    <>
                                      <Tooltip title="Connect with text element">
                                        <div>
                                          <PromptAIConnector
                                            promptSourceId={`${prompt.id}.${property.id}`}
                                          />
                                        </div>
                                      </Tooltip>
                                      <Tooltip title="Add as new text element">
                                        <AddTextButton
                                          onClick={() =>
                                            onAddAiText(
                                              `${prompt.id}.${property.id}`,
                                              promptDependentInputs?.[
                                                `${prompt.id}.${property.id}`
                                              ]?.value,
                                            )
                                          }
                                          type="ghost"
                                        >
                                          <IconWrapper>
                                            <PlusSvg />
                                          </IconWrapper>
                                        </AddTextButton>
                                      </Tooltip>
                                    </>
                                  )}
                              </div>
                            </div>
                          )}
                        </OutputContainer>
                      </DraggableOutput>
                    );
                  }
                  if (property.type === 'array') {
                    return (
                      <DraggableOutput id={property.id}>
                        <OutputContainer
                          key={i}
                          style={{
                            background:
                              `${prompt.id}.${property.id}` ===
                              selectedElement?.promptSourceId
                                ? `${theme.colors.purple}25`
                                : theme.colors.white,
                          }}
                        >
                          <PromptOutputProperty
                            property={property}
                            key={property.id}
                            prompt={prompt}
                            onOutputChanged={onOutputChanged}
                            promptDependentInputs={promptDependentInputs}
                            onAddAiText={onAddAiText}
                            selectedElementId={selectedElement?.promptSourceId}
                          />
                        </OutputContainer>
                      </DraggableOutput>
                    );
                  }
                })}
              </SortableContext>
              <DragOverlay>
                {currentlyDragging ? (
                  <div
                    style={{
                      transition: '1s',
                      transform: 'translate(0, 25%) scale(0.80)',
                      borderRadius: '5px',
                      background: theme.colors.white,
                      padding: 5,
                      overflow: 'hidden',
                      cursor: 'grabbing',
                      boxShadow: `0 0 7px 5px ${theme.colors.green}80`,
                    }}
                  >
                    {
                      <PromptOutputProperty
                        property={orderedPromptOutputProperties[currentlyDragging]}
                        prompt={prompt}
                        onOutputChanged={onOutputChanged}
                        promptDependentInputs={promptDependentInputs}
                        onAddAiText={onAddAiText}
                        selectedElementId={selectedElement?.promptSourceId}
                      />
                    }
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </>
        )}

        {promptDependentInputs &&
          promptDependentInputs[prompt.id] &&
          prompt.promptOutput.type === 'string' && (
            <div>
              <div
                style={{
                  alignItems: 'center',
                  background:
                    `${prompt.id}` === selectedElement?.promptSourceId
                      ? `${theme.colors.purple}25`
                      : theme.colors.white,
                  borderRadius: '5px',
                  padding: '5px',
                  display: 'grid',
                  width: '100%',
                  gridTemplateColumns: '3fr 1fr',
                  backgroundColor: theme.colors.white,
                  boxShadow: theme.effects.floatingCreatorElementShadow,
                  gap: '5px',
                }}
              >
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 'normal' }}>
                    {promptDependentInputs?.[prompt.id] &&
                      promptDependentInputs[prompt.id].value}
                  </p>
                </div>

                {!showTabPaneFullWidth && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      height: '100%',
                    }}
                  >
                    <Tooltip title="Connect with text element">
                      <div>
                        <PromptAIConnector promptSourceId={prompt.id} />
                      </div>
                    </Tooltip>
                    <Tooltip title="Add as new text element">
                      <AddTextButton
                        onClick={() =>
                          onAddAiText(
                            prompt.id,
                            promptDependentInputs?.[prompt.id]?.value,
                          )
                        }
                        type="ghost"
                      >
                        <IconWrapper>
                          <PlusSvg />
                        </IconWrapper>
                      </AddTextButton>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          )}
      </PromptOutputContainer>
    </div>
  );
};

export default PromptOutputBuilder;

const DraggableOutput = ({ id, children }: { id: string; children: React.ReactNode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: id,
    animateLayoutChanges: defaultAnimateLayoutChanges,
  });

  const ref = useRef<HTMLDivElement>();
  const handleSetNodeRef = useCallback(
    (node: HTMLDivElement) => {
      ref.current = node;
      setNodeRef(node);
    },
    [setNodeRef],
  );

  const style = {
    opacity: isDragging ? 0.2 : 1,
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      key={`sort-${id}`}
      ref={handleSetNodeRef}
      style={{ ...style, position: 'relative' }}
    >
      <div
        style={{
          height: 20,
          width: 20,
          position: 'absolute',
          top: 5,
          right: 5,
          borderRadius: 5,
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        {...listeners}
        {...attributes}
        className="handle"
      >
        <Divider
          style={{
            borderTopColor: theme.colors.iconGrey,
            borderTopWidth: 2,
            margin: '2px 0px',
            width: 20,
          }}
        />
        <Divider
          style={{
            borderTopColor: theme.colors.iconGrey,
            borderTopWidth: 2,
            margin: '2px 0px',
            width: 20,
          }}
        />
        <Divider
          style={{
            borderTopColor: theme.colors.iconGrey,
            borderTopWidth: 2,
            margin: '2px 0px',
            width: 20,
          }}
        />
      </div>
      {children}
    </div>
  );
};

const PromptOutputArrayItems = ({
  property,
  promptDependentInputs,
  promptId,
  onAddAiText,
  itemId,
  selectedElementId,
}: {
  promptId: string;
  property: PromptOutputProperty;
  promptDependentInputs: PromptSingleTestCaseResult;
  onAddAiText: (promptSourceId: string, textValue?: string) => void;
  itemId?: string;
  selectedElementId: string | undefined;
}) => {
  const idMatcher = `${promptId}.${property.id}`;
  const showTabPaneFullWidth = useDesignerUIContext(c => c.state.showTabPaneFullWidth);
  // Check that the item id is in the format of promptId.propertyId.itemIndex
  const itemIdCheck = new RegExp(`^${idMatcher}.\\d+$`);

  const relevantItems = Object.keys(promptDependentInputs).filter(key => {
    if (!key.includes(idMatcher)) return false;
    const ids = key.split('.');
    const itemId = ids[3] || undefined;
    if (property.items?.type === 'string') {
      return itemIdCheck.test(key);
    }
    if (property.items?.type === 'object' && itemId) {
      return key.includes(itemId);
    }
  });

  const groupedItems = relevantItems.reduce<ById<{ id: string; value: string }[]>>(
    (acc, cur) => {
      const ids = cur.split('.');
      const index = ids[2];
      const value = promptDependentInputs[cur].value;
      const existing = acc[index] || [];
      return {
        ...acc,
        [index]: [...existing, { id: ids[3], value }],
      };
    },
    {},
  );

  return (
    <div>
      {Object.entries(groupedItems).length === 0 && (
        <span style={{ padding: '5px 0px', color: theme.colors.red }}>
          No items generated
        </span>
      )}
      {Object.entries(groupedItems).map(([itemIndex, itemValues]) => {
        return (
          <div key={itemIndex}>
            <div>
              {itemIndex !== undefined &&
                itemValues.map(itemValue => (
                  <>
                    {(itemId === undefined || itemId === itemValue.id) && (
                      <div
                        key={itemValue.id}
                        style={{
                          margin: '5px 0px',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            padding: '5px',
                            borderRadius: '5px',
                            background:
                              `${promptId}.${property.id}.${itemIndex}${
                                itemValue.id ? `.${itemValue.id}` : ''
                              }` === selectedElementId
                                ? `${theme.colors.purple}25`
                                : theme.colors.white,
                          }}
                        >
                          <p style={{ marginRight: 10, marginBottom: 0 }}>
                            {parseInt(itemIndex) + 1}:
                          </p>
                          <div style={{ flex: 1 }}>{itemValue.value}</div>
                          {!showTabPaneFullWidth && (
                            <>
                              <Tooltip title="Connect with text element">
                                <div>
                                  <PromptAIConnector
                                    promptSourceId={`${promptId}.${
                                      property.id
                                    }.${itemIndex}${
                                      itemValue.id ? `.${itemValue.id}` : ''
                                    }`}
                                  />
                                </div>
                              </Tooltip>
                              <Tooltip title="Add as new text element">
                                <AddTextButton
                                  onClick={() =>
                                    onAddAiText(
                                      `${promptId}.${property.id}.${itemIndex}${
                                        itemValue.id ? `.${itemValue.id}` : ''
                                      }`,
                                      promptDependentInputs?.[
                                        `${promptId}.${property.id}.${itemIndex}${
                                          itemValue.id ? `.${itemValue.id}` : ''
                                        }`
                                      ]?.value,
                                    )
                                  }
                                  type="ghost"
                                >
                                  <IconWrapper>
                                    <PlusSvg />
                                  </IconWrapper>
                                </AddTextButton>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PromptOutputContainer = styled.div`
  & > *:not(:first-child) {
    margin-top: 5px;
  }
  & > *:not(:last-child) {
    margin-bottom: 5px;
  }
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AddTextButton = styled(Button)`
  border-radius: 50%;
  padding: 0px;
  height: 20px;
  width: 20px;
  margin-left: 5px;
  font-size: 14px;
  svg {
    margin: 0;
  }
`;

const StyledButton = styled(Button)<{ $color: string }>`
  padding: 3px 5px;
  border-radius: 50px;
  background-color: ${$props => $props.$color}20;
  color: ${$props => $props.$color};
  width: 25px;
  height: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  span {
    font-size: 20px;
  }

  svg {
    margin: 2px;
  }

  &:hover {
    transition-delay: 0.5s !important;
    transition: width 0.5s ease-in-out;
    color: ${theme.colors.black};
    background-color: ${theme.colors.grey};
  }

  &:focus {
    color: ${theme.colors.black};
    background-color: ${theme.colors.grey};
  }
`;

const OutputContainer = styled.div`
  border-radius: 5px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  gap: 5px;
`;
